import { GeoJsonLayer } from '@deck.gl/layers/typed';
import {
  Box,
  ButtonGroup,
  DialogActions,
  DialogContent,
  FormHelperText,
  styled,
  Tooltip,
} from '@mui/material';
import { Feature, featureCollection, MultiPolygon, Point, Polygon } from '@turf/turf';
import { mergeDefaults } from 'utils/form.utils';
import { useLogEvent } from 'hooks/useLogEvent';
import {
  IWktGeometryFormValues,
  wktGeometrySchema,
} from 'FeatureModules/DataImport/schemas/wktGeometry.schema';
import { DialogDefault, FieldWrapper, ThemeButton, ThemeTypography } from 'DesignSystem';
import CustomMap from 'DesignSystem/Map/CustomMap';
import {
  calculateArea,
  closedWktGeometriesAsFeatures,
  closeOpenWktGeometries,
  featureToWkt,
  wktToFeatureCollection,
} from 'DesignSystem/Map/utils/map.utils';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { ErrorMessage, Field, Form, Formik, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Tbooleanish } from 'types/booleanish.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import copyTextToClipboard from 'utils/clipboard.utils';
import { formatAndRoundNumber } from 'utils/number.utils';
import { getOpenFirstAndLastPointsFromWKT } from 'FeatureModules/DataImport/utils/dataImport.validators';
import FlexBox from 'Components/FlexBox';

interface IWktGeometryEditorDialogProps extends IDefaultDialogProps {
  value?: string | null;
  onSave?: (value?: string | null) => void;
}

enum EWktGeometryEditorViewMode {
  RAW_DATA = 'RAW_DATA',
  MAP_VIEW = 'MAP_VIEW',
}

type TWktGeometryEditorViewMode = `${EWktGeometryEditorViewMode}`;

export const StyledTextField = styled(TextField)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 400,

  '& textarea': {
    minHeight: 300,
    height: 'auto !important',
    color: theme.custom.themeColors.success[100],
    overflow: 'auto !important',

    '&::placeholder': {
      color: theme.custom.themeColors.grayScale[100],
    },
  },
}));

const Container = styled(FlexBox)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

const CultivatedAreasSize = styled(Box)<{ 'map-spacing': Tbooleanish }>(
  ({ theme, 'map-spacing': mapSpacing }) => ({
    position: 'absolute',
    background: theme.custom.themeColors.grayScale[20],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    zIndex: 3,
    right: theme.spacing(mapSpacing === 'true' ? 6 : theme.spacing(2)),
    top: mapSpacing === 'true' ? 56 : theme.spacing(80),
  })
);

const ActionContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  zIndex: 2,
  gap: theme.spacing(1),
  flexDirection: 'column',
  right: theme.spacing(2),
  bottom: theme.spacing(2),
  color: theme.custom.colors.textLight,
}));

const WktGeometryEditorDialog: FC<IWktGeometryEditorDialogProps> = ({
  open,
  value,
  onClose,
  onSave,
}) => {
  const { logEvent } = useLogEvent();

  const [viewMode, setViewMode] = useState<TWktGeometryEditorViewMode>(
    EWktGeometryEditorViewMode.RAW_DATA
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [mapZoom, setMapZoom] = useState<number>();

  const formRef = useRef<FormikProps<IWktGeometryFormValues>>(null);
  const initialValue = mergeDefaults(wktGeometrySchema.default(), { wkt: value });

  const handleCopy = (value: string) => {
    setTooltipOpen(true);
    copyTextToClipboard(value);
  };

  useEffect(() => {
    logEvent('OPENED_WKT_GEOMETRY_EDITOR');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (values: IWktGeometryFormValues) => {
    onSave?.(values.wkt);
    onClose?.();
  };

  const handleClearAndClose = () => {
    onSave?.(null);
    onClose?.();
  };

  const handleCloseOpenGeometry = () => {
    if (!formRef.current?.values.wkt) {
      return;
    }
    formRef.current?.setFieldValue('wkt', closeOpenWktGeometries(formRef.current.values.wkt));
  };

  const handleDrawCreate = (newFeature: Feature<Point | Polygon | MultiPolygon> | undefined) => {
    if (newFeature) {
      formRef.current?.setFieldValue('wkt', featureToWkt(newFeature));
    }
  };

  const handleDrawUpdate = (
    updatedFeature: Feature<Point | Polygon | MultiPolygon> | undefined
  ) => {
    if (updatedFeature) {
      formRef.current?.setFieldValue('wkt', featureToWkt(updatedFeature));
    }
  };

  const handleDrawDelete = (deleteFeature: Feature<Point | Polygon | MultiPolygon> | undefined) => {
    if (deleteFeature) {
      formRef.current?.setFieldValue('wkt', null);
    }
  };

  const getWktSizeInHectares = (wkt: string) => {
    try {
      return formatAndRoundNumber(calculateArea(wktToFeatureCollection(wkt)), 2);
    } catch {
      return '0';
    }
  };

  return (
    <DialogDefault
      open={open}
      title="Edit plot geometry"
      fullWidth
      iconName="polygon-mapping"
      maxWidth="md"
      onClose={onClose}
    >
      <Formik<IWktGeometryFormValues>
        innerRef={formRef}
        initialValues={initialValue}
        isInitialValid={wktGeometrySchema.isValidSync(initialValue)}
        validationSchema={wktGeometrySchema}
        onSubmit={handleSubmit}
      >
        {({ values, isValid }) => {
          const openWktGeometryPoints = getOpenFirstAndLastPointsFromWKT(values.wkt);
          // The map line layer has some issues on the globes curvature when zoomed out, so only show
          // the layer when zoomed in enough
          const mapLayers =
            mapZoom && mapZoom > 12 && openWktGeometryPoints.length > 0
              ? [
                  new GeoJsonLayer({
                    id: 'open-polygon-wkt-layer',
                    data: openWktGeometryPoints.map(points => ({
                      type: 'Feature',
                      properties: {},
                      geometry: {
                        type: 'LineString',
                        coordinates: points,
                      },
                    })),
                    stroked: true,
                    filled: true,
                    getLineWidth: 3,
                    getLineColor: [255, 0, 0, 255],
                    lineWidthMinPixels: 2,
                    lineWidthMaxPixels: 6,
                    pickable: true,
                  }),
                ]
              : [];

          return (
            <Form>
              <DialogContent>
                <Container>
                  <ButtonGroup>
                    <ThemeButton
                      className={viewMode === EWktGeometryEditorViewMode.RAW_DATA ? 'selected' : ''}
                      onClick={() => setViewMode(EWktGeometryEditorViewMode.RAW_DATA)}
                      typographyColor="ALMOST_BLACK"
                    >
                      Raw data
                    </ThemeButton>
                    <Tooltip
                      title={
                        isValid || openWktGeometryPoints.length
                          ? ''
                          : 'Please provide valid WKT geometry data to use map view.'
                      }
                    >
                      <Box>
                        <ThemeButton
                          disabled={!isValid && !openWktGeometryPoints.length}
                          className={
                            viewMode === EWktGeometryEditorViewMode.MAP_VIEW ? 'selected' : ''
                          }
                          typographyColor="ALMOST_BLACK"
                          onClick={() => setViewMode(EWktGeometryEditorViewMode.MAP_VIEW)}
                        >
                          Map view
                        </ThemeButton>
                      </Box>
                    </Tooltip>
                  </ButtonGroup>

                  {(isValid || !openWktGeometryPoints.length) &&
                    values.wkt &&
                    viewMode === EWktGeometryEditorViewMode.MAP_VIEW && (
                      <CultivatedAreasSize map-spacing="true">
                        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                          Total area
                        </ThemeTypography>
                        <ThemeTypography variant="BODY_SMALL">
                          {getWktSizeInHectares(values.wkt)} ha
                        </ThemeTypography>
                      </CultivatedAreasSize>
                    )}

                  {viewMode === EWktGeometryEditorViewMode.MAP_VIEW ? (
                    <Box height={350} width="100%">
                      <CustomMap
                        layers={mapLayers}
                        defaultDrawFeatures={
                          values.wkt
                            ? featureCollection(closedWktGeometriesAsFeatures(values.wkt))
                            : undefined
                        }
                        mapStyle="satellite"
                        config={{
                          enableDrawing: true,
                          enableCenterButton: true,
                          enableMapStyleToggle: true,
                          hideDrawingButtons: !!values.wkt,
                        }}
                        onDrawCreate={handleDrawCreate}
                        onDrawUpdate={handleDrawUpdate}
                        onDrawDelete={handleDrawDelete}
                        onZoom={event => setMapZoom(event.viewState.zoom)}
                      />
                      <ErrorMessage name="wkt">
                        {message => <FormHelperText error>{message}</FormHelperText>}
                      </ErrorMessage>
                    </Box>
                  ) : (
                    <Box position="relative" width="100%">
                      {values.wkt && (
                        <ActionContainer>
                          <Tooltip
                            title="Content copied to clipboard!"
                            onClose={() => setTooltipOpen(false)}
                            open={tooltipOpen}
                            leaveDelay={800}
                          >
                            <ThemeButton
                              color="BLUE_ICE"
                              startIcon={<Icon name="copy" />}
                              onClick={() => handleCopy(values.wkt || '')}
                            >
                              Copy data
                            </ThemeButton>
                          </Tooltip>
                        </ActionContainer>
                      )}

                      <FieldWrapper
                        fullWidth
                        label="Geometry (WKT)"
                        info={
                          <>
                            Read more about WKT format{' '}
                            <a
                              href="https://en.wikipedia.org/wiki/Well-known_text_representation_of_geometry"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              here
                            </a>
                          </>
                        }
                        tooltip={{
                          helperText: (
                            <>
                              Well-known text (WKT) is a text markup language for
                              representing vector geometry objects. You can add and edit plot
                              geo-data in the following formats:
                              <ul style={{ paddingLeft: 24, margin: 0 }}>
                                <li>POINT (30 10)</li>
                                <li>POLYGON ((35 10, 10 20, 35 10), (20 30, 35 20, 20 30))</li>
                                <li>
                                  MULTIPOLYGON (((40 40, 40 30, 40 40)), ((20 35, 10 30, 10 10, 30
                                  5, 45 20, 20 35), (30 20, 20 15, 20 25, 30 20)))
                                </li>
                              </ul>
                            </>
                          ),
                          variant: 'INFO',
                        }}
                      >
                        <Field
                          name="wkt"
                          autoFocus
                          fullWidth
                          component={StyledTextField}
                          multiline
                          minRows={15}
                          placeholder="POLYGON (( 52.508749 13.462562, 52.508749 13.462562 "
                        />
                      </FieldWrapper>
                    </Box>
                  )}
                </Container>
              </DialogContent>

              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <FlexBox gap={2}>
                  <ThemeButton
                    startIcon={<Icon name="delete" />}
                    color="BLUE_ICE"
                    onClick={handleClearAndClose}
                  >
                    Clear cell content
                  </ThemeButton>
                  {openWktGeometryPoints.length > 0 && (
                    <ThemeButton
                      startIcon={<Icon name="polygon-mapping" />}
                      color="BLUE_ICE"
                      onClick={handleCloseOpenGeometry}
                    >
                      Close open geometry
                    </ThemeButton>
                  )}
                </FlexBox>
                <FlexBox gap={2}>
                  <ThemeButton color="BLUE_ICE" onClick={onClose}>
                    Cancel
                  </ThemeButton>
                  <ThemeButton color="PRIMARY" type="submit" disabled={!isValid}>
                    Save
                  </ThemeButton>
                </FlexBox>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </DialogDefault>
  );
};

export default WktGeometryEditorDialog;
